<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells><app-button @click.native="onShowModal(null)">Add contact</app-button></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #h__full_name><div class="table-default__left">Full name</div></template>
        <template #h__position><div class="table-default__left">Position</div></template>
        <template #full_name="{ row }"><div class="table-default__left">{{ row.full_name }}</div></template>
        <template #communication="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.email, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.email)"
              class="table-default__activity table-default__activity--mail"
              :class="{'table-default__activity--active': row.email}"
            >
            </button>
            <button
              v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.phone)"
              class="table-default__activity table-default__activity--phone"
              :class="{'table-default__activity--active': row.phone}"
            >
            </button>
            <a
              :href="`https://wa.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--wa"
              :class="{'table-default__activity--active': row.phone && row.whatsapp_available}"
            >
            </a>
          </div>
        </template>
        <template #position="{ row }"><div class="table-default__left">{{ row.position || '-' }}</div></template>
        <template #responsible_for_accounting="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.responsible_for_accounting ? '#77b017' : '#5f5f5f' }"
          >
            {{ row.responsible_for_accounting ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
    </template>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete contact
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this contact?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteContact" ref="delete_account">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="create_contact"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('create_contact')" class="popup__close" type="button"></button>
        <div class="popup__title">
          {{ is_popup_edit ? 'Edit' : 'Add' }} contact
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckContactForm">
            <app-form-group label="Full name" label-for="full_name" required>
              <app-input
                v-model="form.full_name"
                id="full_name"
                placeholder="Full name"
                name="full_name"
                :error="$v.form.full_name.$error"
              />
              <template #error>
                <div v-if="$v.form.full_name.$dirty && !$v.form.full_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="E-mail" label-for="email">
              <app-input
                v-model="form.email"
                id="email"
                type="email"
                placeholder="E-mail"
                name="email"
                :error="$v.form.email.$error"
              />
              <template #error>
                <div v-if="$v.form.email.$dirty && !$v.form.email.email">Enter the correct e-mail format</div>
              </template>
            </app-form-group>
            <app-form-group label="Phone">
              <app-phone
                @change.native="onValidatePhone"
                v-model="form.phone"
                placeholder="+255 786 350 350"
                :error="$v.form.phone.$dirty && form.phone === 0"
              />
              <template #error>
                <div v-if="$v.form.phone.$dirty && form.phone === 0">
                  Invalid phone number format
                </div>
              </template>
            </app-form-group>
            <app-form-group>
              <app-checkbox :readonly="!form.phone" v-model="form.whatsapp_available" label="Whatsapp" />
            </app-form-group>
            <app-form-group label="Position" label-for="position">
              <app-input
                v-model="form.position"
                id="position"
                placeholder="Position"
                name="position"
              />
            </app-form-group>
            <app-form-group>
              <app-checkbox v-model="form.responsible_for_accounting" label="Responsible for accounting" />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit_contact">
                {{ is_popup_edit ? 'Save' : 'Add' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteAdministrativeSupplierContactPerson,
  getAdministrativeSupplierContactPersons,
  patchAdministrativeSupplierContactPerson,
  postAdministrativeSupplierContactPerson,
} from '@/http'
import { email, required } from 'vuelidate/lib/validators'

export default {
  name: 'SuppliersDetailContactPersons',
  data() {
    return {
      page_loaded: false,
      is_popup_edit: false,
      contact_to_delete: null,
      table: {
        items: [],
        columns: ['full_name', 'communication', 'position', 'responsible_for_accounting', 'controls'],
        options: {
          headings: {
            full_name: 'Full name',
            communication: 'Communication',
            position: 'Position',
            responsible_for_accounting: 'Responsible for accounting',
            controls: '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      form: {},
    }
  },
  validations: {
    form: {
      full_name: { required },
      email: { email },
      phone: {},
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      getAdministrativeSupplierContactPersons(Number(this.$route.params.id))
        .then(res => {
          this.table.items = res.data
          this.page_loaded = true
        })
    },
    onCheckContactForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendContactForm()
      }
    },
    sendContactForm() {
      this.$refs.submit_contact.preload = true
      const data = {...this.form, whatsapp_available: !!this.form.phone }
      if (!this.is_popup_edit) {
        postAdministrativeSupplierContactPerson(Number(this.$route.params.id), data)
          .then(() => {
            this.fetchItems()
            this.$refs.submit_contact.preload = false
            this.$notify({
              type: 'success',
              title: 'Contact added',
            })
            this.$modal.hide('create_contact')
          })
          .catch(() => { this.$refs.submit_contact.preload = false })
      } else {
        patchAdministrativeSupplierContactPerson(Number(this.$route.params.id), this.form.id, data)
          .then(() => {
            this.fetchItems()
            this.$refs.submit_contact.preload = false
            this.$notify({
              type: 'success',
              title: 'Contact saved',
            })
            this.$modal.hide('create_contact')
          })
          .catch(() => { this.$refs.submit_contact.preload = false })
      }
    },
    onShowModal(row) {
      if (row) {
        this.is_popup_edit = true
        this.form = { ...row }
      } else {
        this.is_popup_edit = false
        this.form = {}
      }
      this.$v.form.$reset()
      this.$modal.show('create_contact')
    },
    onShowDeleteModal(id) {
      this.contact_to_delete = id
      this.$modal.show('delete-modal')
    },
    onDeleteContact() {
      this.$refs.delete_account.preload = true
      deleteAdministrativeSupplierContactPerson(this.$route.params.id, this.contact_to_delete)
        .then(() => {
          this.$refs.delete_account.preload = false
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Contact deleted',
          })
          this.$modal.hide('delete-modal')
        })
        .catch(() => { this.$refs.delete_account.preload = false })
    },
    onValidatePhone() {
      if (this.form.phone === 0) this.form.whatsapp_available = false
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  }
}
</script>
